import React, { useState, useEffect } from 'react'
import { leadsApi, contentApi } from '../../services/api'
import { ImageManager } from './ImageManager'
import { ProductManager } from './ProductManager'
import VideoManager from './VideoManager'

interface DashboardProps {
  user: any
  onLogout: () => void
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('leads')
  const [leads, setLeads] = useState<any[]>([])
  const [stats, setStats] = useState<any>(null)
  const [content, setContent] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditingContent, setIsEditingContent] = useState(false)
  const [editingContent, setEditingContent] = useState<any>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showImageManager, setShowImageManager] = useState(false)
  const [selectedCollection, setSelectedCollection] = useState<'boys' | 'girls' | null>(null)
  const [showVideoManager, setShowVideoManager] = useState(false)
  const [showProductManager, setShowProductManager] = useState(false)
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [leadsResponse, statsResponse, contentResponse] = await Promise.all([
        leadsApi.getAll(),
        leadsApi.getStats(),
        contentApi.getAll()
      ])

      setLeads(leadsResponse.leads || [])
      setStats(statsResponse || {})
      setContent(contentResponse || {})
    } catch (error) {
      console.error('❌ Error al cargar datos:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const reloadContent = async () => {
    try {
      const contentResponse = await contentApi.getAll()
      setContent(contentResponse || {})
    } catch (error) {
      console.error('Error al recargar contenido:', error)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleEditContent = () => {
    // Crear objeto de edición con valores por defecto si no existen
    const editingData = {
      hero: {
        title: content?.hero?.title || '',
        subtitle: content?.hero?.subtitle || '',
        description: content?.hero?.description || '',
        cta_primary: content?.hero?.cta_primary || '',
        cta_secondary: content?.hero?.cta_secondary || ''
      },
      collections: {
        title: content?.collections?.title || '',
        subtitle: content?.collections?.subtitle || '',
        boys: {
          title: content?.collections?.boys?.title || '',
          description: content?.collections?.boys?.description || ''
        },
        girls: {
          title: content?.collections?.girls?.title || '',
          description: content?.collections?.girls?.description || ''
        }
      },
      about: {
        title: content?.about?.title || '',
        description: content?.about?.description || '',
        stats: {
          years: content?.about?.stats?.years || 0,
          families: content?.about?.stats?.families || 0
        }
      }
    }
    
    setEditingContent(editingData)
    setIsEditingContent(true)
  }

  const handleSaveContent = async () => {
    setIsSaving(true)
    try {
      // Actualizar cada sección por separado
      const sections = ['hero', 'collections', 'about']

      for (const section of sections) {
        if (editingContent[section]) {
          await contentApi.update(section, editingContent[section])
        }
      }

      // Recargar contenido desde el servidor
      await reloadContent()

      // Disparar evento para que la landing page recargue el contenido
      window.dispatchEvent(new CustomEvent('contentUpdated'))

      setIsEditingContent(false)
      setNotification({ type: 'success', message: '✅ Contenido guardado exitosamente. Los cambios se han aplicado a la landing page.' })

      // Auto-desaparecer la notificación después de 3 segundos
      setTimeout(() => {
        setNotification(null)
      }, 3000)

    } catch (error) {
      setNotification({ type: 'error', message: '❌ Error al guardar el contenido. Por favor, inténtalo de nuevo.' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditingContent(false)
    setEditingContent(null)
  }

  const updateEditingContent = (section: string, field: string, value: string, subfield?: string) => {
    setEditingContent((prev: any) => {
      const newContent = { ...prev }
      
      if (subfield) {
        // Para campos anidados como collections.boys.description
        if (!newContent[section]) {
          newContent[section] = {}
        }
        if (!newContent[section][field]) {
          newContent[section][field] = {}
        }
        newContent[section][field][subfield] = value
      } else {
        // Para campos simples
        if (!newContent[section]) {
          newContent[section] = {}
        }
        newContent[section][field] = value
      }
      
      return newContent
    })
  }

  const handleManageImages = (collection: 'boys' | 'girls') => {
    setSelectedCollection(collection)
    setShowImageManager(true)
  }

  const handleCloseImageManager = () => {
    setShowImageManager(false)
    setSelectedCollection(null)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-verano-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Panel de Administración
              </h1>
              <p className="text-gray-600">Bienvenido, Gonzalo Gabriel</p>
            </div>
            <button
              onClick={onLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Notification Banner */}
      {notification && (
        <div className={`${notification.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'} border-b px-4 py-3`}>
          <div className="max-w-7xl mx-auto">
            {notification.message}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leads'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leads ({leads.length})
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Estadísticas
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'content'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Contenido
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'images'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Imágenes
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'videos'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Videos
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-verano-primary text-verano-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Productos
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'leads' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Lista de Leads
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Suscriptores que han mostrado interés en Jenny Kids
                </p>
              </div>
              <ul className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <li key={lead.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-verano-primary flex items-center justify-center">
                              <span className="text-white font-medium">
                                {lead.name ? lead.name.charAt(0).toUpperCase() : '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.name || 'Sin nombre'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-sm text-gray-500">
                            {lead.phone}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(lead.created_at)}
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {lead.source}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-verano-primary rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Leads
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.total || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">📅</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Este Mes
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.thisMonth || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">📊</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Esta Semana
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.thisWeek || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">🔥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Hoy
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.today || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Contenido de la Landing Page
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Gestiona el contenido editable de la página
                </p>
              </div>
              <div className="px-4 py-5 sm:px-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900">Sección Hero</h4>
                    <p className="text-sm text-gray-600">
                      Título: {content?.hero?.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      Subtítulo: {content?.hero?.subtitle}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-md font-medium text-gray-900">Colecciones</h4>
                    <p className="text-sm text-gray-600">
                      Título: {content?.collections?.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      Subtítulo: {content?.collections?.subtitle}
                    </p>
                    <p className="text-sm text-gray-600">
                      Colección Niños: {content?.collections?.boys?.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      Descripción Niños: {content?.collections?.boys?.description}
                    </p>
                    <p className="text-sm text-gray-600">
                      Colección Niñas: {content?.collections?.girls?.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      Descripción Niñas: {content?.collections?.girls?.description}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-md font-medium text-gray-900">Sobre Nosotros</h4>
                    <p className="text-sm text-gray-600">
                      Título: {content?.about?.title}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex gap-4">
                  <button 
                    onClick={handleEditContent}
                    className="bg-verano-primary hover:bg-verano-secondary text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Editar Contenido
                  </button>
                  <button 
                    onClick={() => window.open('/', '_blank')}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Ver Landing Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de Edición de Contenido */}
      {isEditingContent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Editar Contenido de la Landing Page
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Sección Hero */}
                <div className="border rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Sección Hero</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Título</label>
                      <input
                        type="text"
                        value={editingContent?.hero?.title || ''}
                        onChange={(e) => updateEditingContent('hero', 'title', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subtítulo</label>
                      <input
                        type="text"
                        value={editingContent?.hero?.subtitle || ''}
                        onChange={(e) => updateEditingContent('hero', 'subtitle', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección Colecciones */}
                <div className="border rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Colecciones</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Título</label>
                      <input
                        type="text"
                        value={editingContent?.collections?.title || ''}
                        onChange={(e) => updateEditingContent('collections', 'title', e.target.value)}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subtítulo</label>
                      <input
                        type="text"
                        value={editingContent?.collections?.subtitle || ''}
                        onChange={(e) => updateEditingContent('collections', 'subtitle', e.target.value)}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Título Colección Niños</label>
                      <input
                        type="text"
                        value={editingContent?.collections?.boys?.title || ''}
                        onChange={(e) => updateEditingContent('collections', 'boys', e.target.value, 'title')}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Descripción Colección Niños</label>
                      <textarea
                        rows={3}
                        value={editingContent?.collections?.boys?.description || ''}
                        onChange={(e) => updateEditingContent('collections', 'boys', e.target.value, 'description')}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200 resize-vertical"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Título Colección Niñas</label>
                      <input
                        type="text"
                        value={editingContent?.collections?.girls?.title || ''}
                        onChange={(e) => updateEditingContent('collections', 'girls', e.target.value, 'title')}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Descripción Colección Niñas</label>
                      <textarea
                        rows={3}
                        value={editingContent?.collections?.girls?.description || ''}
                        onChange={(e) => updateEditingContent('collections', 'girls', e.target.value, 'description')}
                        className="mt-1 block w-full border-2 border-gray-400 rounded-lg shadow-lg focus:ring-2 focus:ring-verano-primary focus:border-verano-primary sm:text-sm bg-white hover:border-gray-500 transition-colors duration-200 resize-vertical"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección Sobre Nosotros */}
                <div className="border rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Sobre Nosotros</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Título</label>
                      <input
                        type="text"
                        value={editingContent?.about?.title || ''}
                        onChange={(e) => updateEditingContent('about', 'title', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Descripción</label>
                      <textarea
                        value={editingContent?.about?.description || ''}
                        onChange={(e) => updateEditingContent('about', 'description', e.target.value)}
                        rows={4}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Años en el negocio</label>
                        <input
                          type="number"
                          value={editingContent?.about?.stats?.years || 0}
                          onChange={(e) => updateEditingContent('about', 'stats', { ...editingContent?.about?.stats, years: parseInt(e.target.value) || 0 })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Familias atendidas</label>
                        <input
                          type="number"
                          value={editingContent?.about?.stats?.families || 0}
                          onChange={(e) => updateEditingContent('about', 'stats', { ...editingContent?.about?.stats, families: parseInt(e.target.value) || 0 })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-verano-primary focus:border-verano-primary sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveContent}
                  disabled={isSaving}
                  className="bg-verano-primary hover:bg-verano-secondary text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección de Imágenes */}
      {activeTab === 'images' && (
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Gestión de Imágenes de Colecciones
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Administra las imágenes de las colecciones de niños y niñas
              </p>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Colección Niños */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Colección Niños</h4>
                    <span className="text-2xl">👦</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Gestiona las imágenes de la colección para niños
                  </p>
                  <button
                    onClick={() => handleManageImages('boys')}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Gestionar Imágenes
                  </button>
                </div>

                {/* Colección Niñas */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Colección Niñas</h4>
                    <span className="text-2xl">👧</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Gestiona las imágenes de la colección para niñas
                  </p>
                  <button
                    onClick={() => handleManageImages('girls')}
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Gestionar Imágenes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección de Videos */}
      {activeTab === 'videos' && (
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Gestión de Videos de la Tienda
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Administra el video promocional que se muestra en la sección "Nuestra Tienda"
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Video de la Tienda
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Sube un video promocional que se mostrará en la sección "Nuestra Tienda" de la landing page.
                  El video se reproducirá automáticamente en bucle.
                </p>
                <button
                  onClick={() => setShowVideoManager(true)}
                  className="bg-verano-primary text-white px-6 py-3 rounded-md hover:bg-verano-primary/90 font-medium"
                >
                  Gestionar Videos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección de Productos */}
      {activeTab === 'products' && (
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Catálogo de Productos
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Administra los productos de Jenny Kids con precios, tallas e imágenes
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <div className="mb-4">
                  <span className="text-5xl">🏷️</span>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Gestión de Productos
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Crea, edita y elimina productos del catálogo. Administra precios, tallas, stock e imágenes.
                  Los cambios se verán en tiempo real en la landing page.
                </p>
                <button
                  onClick={() => setShowProductManager(true)}
                  className="bg-verano-primary text-white px-6 py-3 rounded-md hover:bg-verano-primary/90 font-medium"
                >
                  Gestionar Productos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestión de imágenes */}
      {showImageManager && selectedCollection && (
        <ImageManager
          collection={selectedCollection}
          onClose={handleCloseImageManager}
        />
      )}

      {/* Modal de gestión de videos */}
      {showVideoManager && (
        <VideoManager
          onClose={() => setShowVideoManager(false)}
        />
      )}

      {/* Modal de gestión de productos */}
      {showProductManager && (
        <ProductManager
          onClose={() => setShowProductManager(false)}
        />
      )}
    </div>
  )
}

export default Dashboard

